import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { RowSelectionState } from "@tanstack/react-table";
import { ListWorkspaceScheduleApplicationsQuery } from "@common/schemas/domains/accounting/scheduleApplicationList.schema";
import { formatDateOnly } from "@helpers/date";
import { useStateAwareNavigate } from "@hooks/useStateAwareNavigate";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { ScheduleType } from "@common/types/domains/accounting/schedule";
import { useWorkspaceScheduleApplications } from "@api/scheduleTemplate.api";
import { WORKSPACE_USER_CACHE_KEYS } from "@hooks/useWorkspaceUserCache";
import {
    EMPTY_SCHEDULE_APPLICATIONS_FILTERS,
    ScheduleApplicationsFilters,
    mapApplicationToDisplayRows,
    matchesLineItemLabels,
} from "./scheduleApplicationRowHelpers";
import {
    buildScheduleSelectionMonths,
    getSelectedScheduleRows,
    summarizeSelectedSchedules,
} from "./scheduleApplicationSelectionTotals";
import { useScheduleApplicationsBulkBilling } from "./useScheduleApplicationsBulkBilling";
import { useScheduleApplicationsBulkChangePolicy } from "./useScheduleApplicationsBulkChangePolicy";
import { useScheduleApplicationsBulkLifecycle } from "./useScheduleApplicationsBulkLifecycle";
import { useScheduleApplicationsBulkMilestones } from "./useScheduleApplicationsBulkMilestones";
import { buildFailedRowSelection } from "./scheduleLifecycleBulkHelpers";
import { ScheduleApplicationsFooter } from "./ScheduleApplicationsFooter";
import { useScheduleApplicationBulkCommandActions } from "./useScheduleApplicationBulkCommandActions";
import {
    SCHEDULE_APPLICATIONS_PAGE_DEFAULT_VIEW,
    ScheduleApplicationsTable,
} from "./ScheduleApplicationsTable";
import { toSortedPolicyFilterOptions } from "../policyFilterOptions";
import { useUrlScopedTemplateIds } from "../useUrlScopedTemplateIds";
import {
    useScheduleHubScope,
    useScheduleHubTemplates,
} from "../ScheduleHubScopeProvider";

const SCHEDULE_APPLICATIONS_PAGE_SIZE = 50;

function buildListQuery(options: {
    filters: ScheduleApplicationsFilters;
    search: string;
    applicationId: string | null;
    applicationIds: string[];
    page: number;
    scheduleType: ScheduleType | null;
}): ListWorkspaceScheduleApplicationsQuery {
    const { filters, search, applicationId, applicationIds, page } = options;
    const trimmedSearch = search.trim();
    return {
        ...(options.scheduleType
            ? { scheduleTypes: [options.scheduleType] }
            : {}),
        ...(filters.templateIds.length > 0
            ? { templateIds: filters.templateIds }
            : {}),
        ...(filters.groups.length > 0 ? { groups: filters.groups } : {}),
        ...(filters.sourceTypes.length > 0
            ? { sourceTypes: filters.sourceTypes }
            : {}),
        ...(filters.revenueSubModes.length > 0
            ? { revenueSubModes: filters.revenueSubModes }
            : {}),
        ...(filters.expenseSubModes.length > 0
            ? { expenseSubModes: filters.expenseSubModes }
            : {}),
        ...(filters.entityIds.length > 0
            ? { entityIds: filters.entityIds }
            : {}),
        ...(filters.lifecycleStatuses.length > 0
            ? { lifecycleStatuses: filters.lifecycleStatuses }
            : {}),
        ...(filters.billingStatuses.length > 0
            ? { billingStatuses: filters.billingStatuses }
            : {}),
        ...(filters.counterpartyNames.length > 0
            ? { counterpartyNames: filters.counterpartyNames }
            : {}),
        ...(filters.lineItemLabels.length > 0
            ? { lineItemLabels: filters.lineItemLabels }
            : {}),
        ...(filters.classIds.length > 0 ? { classIds: filters.classIds } : {}),
        ...(filters.periodStart ? { periodStart: filters.periodStart } : {}),
        ...(filters.periodEnd ? { periodEnd: filters.periodEnd } : {}),
        ...(trimmedSearch ? { search: trimmedSearch } : {}),
        ...(applicationId ? { applicationId } : {}),
        ...(applicationIds.length > 0 ? { applicationIds } : {}),
        page,
        limit: SCHEDULE_APPLICATIONS_PAGE_SIZE,
    };
}

interface ScheduleApplicationsTabProps {
    /** Policy scope carried in the URL; the Policy filter mirrors it both ways. */
    templateId: string | null;
    onTemplateIdChange: (templateId: string | null) => void;
    /** One schedule, from a transaction or invoice "View journal entries" link. */
    applicationId: string | null;
    onApplicationIdChange: (applicationId: string | null) => void;
    /** The schedules behind a Schedules report cell. */
    applicationIds: string[];
    onApplicationIdsClear: () => void;
    counterpartyName: string | null;
    onCounterpartyNameChange: (counterpartyName: string | null) => void;
    periodStart: string | null;
    periodEnd: string | null;
    onPeriodChange: (period: {
        start: string | null;
        end: string | null;
    }) => void;
    tabNavigationTop: ReactNode;
    viewCacheKey?: WORKSPACE_USER_CACHE_KEYS;
    isFullPage?: boolean;
    onOpenReport?: (url: string) => void;
}

function buildFilters({
    templateId,
    counterpartyName,
    periodStart,
    periodEnd,
}: {
    templateId: string | null;
    counterpartyName: string | null;
    periodStart: string | null;
    periodEnd: string | null;
}): ScheduleApplicationsFilters {
    return {
        ...EMPTY_SCHEDULE_APPLICATIONS_FILTERS,
        templateIds: templateId ? [templateId] : [],
        counterpartyNames: counterpartyName ? [counterpartyName] : [],
        periodStart,
        periodEnd,
    };
}

export const ScheduleApplicationsTab = ({
    templateId,
    onTemplateIdChange,
    applicationId,
    onApplicationIdChange,
    applicationIds,
    onApplicationIdsClear,
    counterpartyName,
    onCounterpartyNameChange,
    periodStart,
    periodEnd,
    onPeriodChange,
    tabNavigationTop,
    viewCacheKey = WORKSPACE_USER_CACHE_KEYS.SCHEDULE_APPLICATIONS_PAGE_VIEW_CACHE_KEY,
    isFullPage = true,
    onOpenReport,
}: ScheduleApplicationsTabProps) => {
    const navigate = useStateAwareNavigate();
    const openReport = onOpenReport ?? navigate;
    const applicationIdsKey = applicationIds.join("_");
    const [filters, setFilters] = useState<ScheduleApplicationsFilters>(() =>
        buildFilters({ templateId, counterpartyName, periodStart, periodEnd }),
    );
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const scheduleType = useScheduleHubScope()?.scheduleType ?? null;
    const query = useMemo(
        () =>
            buildListQuery({
                filters,
                search,
                applicationId,
                applicationIds: applicationIdsKey
                    ? applicationIdsKey.split("_")
                    : [],
                page,
                scheduleType,
            }),
        [filters, search, applicationId, applicationIdsKey, page, scheduleType],
    );
    const {
        applications,
        total,
        pageCount,
        counterpartyNames,
        lineItemLabels,
        isLoading,
        isFetching,
    } = useWorkspaceScheduleApplications(query);
    const { data: templates } = useScheduleHubTemplates({
        includeArchived: true,
        includeMilestone: true,
    });

    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
    // Any change to what matches lands the user back on page 1 ...
    useEffect(() => {
        setPage(1);
    }, [filters, search, applicationId, applicationIdsKey]);
    useEffect(() => {
        setRowSelection({});
    }, [filters, search, applicationId, applicationIdsKey, page]);
    // ... and a shrinking result set never strands them past the last page.
    useEffect(() => {
        setPage((current) => (current > pageCount ? pageCount : current));
    }, [pageCount]);

    const rows = useMemo(
        () =>
            (applications ?? []).flatMap((application) =>
                mapApplicationToDisplayRows(application, {
                    asOfDate: filters.periodEnd ?? undefined,
                }),
            ),
        [applications, filters.periodEnd],
    );
    const displayRows = useMemo(() => {
        if (filters.lineItemLabels.length === 0) {
            return rows;
        }
        return rows.filter((row) =>
            matchesLineItemLabels(row, filters.lineItemLabels),
        );
    }, [filters.lineItemLabels, rows]);
    const selectedRows = useMemo(
        () => getSelectedScheduleRows({ rows: displayRows, rowSelection }),
        [displayRows, rowSelection],
    );
    const summarizedRows = selectedRows.length > 0 ? selectedRows : displayRows;
    const totals = useMemo(
        () => summarizeSelectedSchedules(summarizedRows),
        [summarizedRows],
    );
    const months = useMemo(
        () =>
            buildScheduleSelectionMonths({
                selectedRows: summarizedRows,
                applications: applications ?? [],
                periodStart: filters.periodStart,
                periodEnd: filters.periodEnd,
            }),
        [applications, filters.periodEnd, filters.periodStart, summarizedRows],
    );
    const clearRowSelection = useCallback(() => setRowSelection({}), []);
    const bulkBilling = useScheduleApplicationsBulkBilling({
        selectedRows,
        onCompleted: clearRowSelection,
    });
    const { schedulesMilestoneBased: isMilestoneEnabled } = useTypedFlags();
    const bulkMilestones = useScheduleApplicationsBulkMilestones({
        rows: displayRows,
        rowSelection,
        isEnabled: isMilestoneEnabled,
        onCompleted: clearRowSelection,
    });
    const reselectFailedRows = useCallback(
        (failedApplicationIds: string[]) =>
            setRowSelection(
                buildFailedRowSelection({
                    rows: displayRows,
                    failedApplicationIds,
                }),
            ),
        [displayRows],
    );
    const bulkLifecycle = useScheduleApplicationsBulkLifecycle({
        selectedRows,
        onCompleted: reselectFailedRows,
    });
    const bulkChangePolicy = useScheduleApplicationsBulkChangePolicy({
        selectedRows,
        clearSelection: clearRowSelection,
    });
    const { handleOpenBulkActions } = useScheduleApplicationBulkCommandActions({
        selectedRows,
        periodStart: filters.periodStart,
        periodEnd: filters.periodEnd,
        onOpenReport: openReport,
        onMarkBilled: bulkBilling.onMarkBilled,
        markBilledDisabledReason: bulkBilling.markBilledDisabledReason,
        selectedMilestoneRowCount: bulkMilestones.selectedMilestoneRowCount,
        onMilestoneAchieved: bulkMilestones.onMilestoneAchieved,
        milestoneAchievedDisabledReason:
            bulkMilestones.milestoneAchievedDisabledReason,
        lifecycleActions: bulkLifecycle.actions,
        changePolicyOptions: bulkChangePolicy.options,
        changePolicyDisabledReason: bulkChangePolicy.disabledReason,
    });
    const templateOptions = useMemo(
        () => toSortedPolicyFilterOptions(templates ?? []),
        [templates],
    );
    const footer = useMemo(
        () => (
            <ScheduleApplicationsFooter
                total={total}
                currentPage={page}
                pageCount={pageCount}
                onPageChange={setPage}
                isFetching={isFetching}
                selectedCount={
                    selectedRows.length +
                    bulkMilestones.selectedMilestoneRowCount
                }
                totals={totals}
                months={months}
                remainingAsOfLabel={
                    filters.periodEnd ? formatDateOnly(filters.periodEnd) : null
                }
                onClearSelection={() => setRowSelection({})}
                onOpenBulkActions={handleOpenBulkActions}
            />
        ),
        [
            bulkMilestones.selectedMilestoneRowCount,
            filters.periodEnd,
            handleOpenBulkActions,
            isFetching,
            months,
            page,
            pageCount,
            selectedRows.length,
            totals,
            total,
        ],
    );

    const syncTemplateIdsToUrl = useUrlScopedTemplateIds({
        templateId,
        onTemplateIdChange,
        setFilters,
    });

    useEffect(() => {
        if (!counterpartyName) {
            return;
        }
        setFilters((current) =>
            current.counterpartyNames.length === 1 &&
            current.counterpartyNames[0] === counterpartyName
                ? current
                : { ...current, counterpartyNames: [counterpartyName] },
        );
    }, [counterpartyName]);

    useEffect(() => {
        if (!periodStart && !periodEnd) {
            return;
        }
        setFilters((current) =>
            current.periodStart === periodStart &&
            current.periodEnd === periodEnd
                ? current
                : { ...current, periodStart, periodEnd },
        );
    }, [periodEnd, periodStart]);

    const handleFiltersChange = useCallback(
        (nextFilters: ScheduleApplicationsFilters) => {
            setFilters(nextFilters);
            if (applicationId) {
                onApplicationIdChange(null);
            }
            if (applicationIdsKey) {
                onApplicationIdsClear();
            }
            syncTemplateIdsToUrl(nextFilters.templateIds);
            const nextCounterpartyName =
                nextFilters.counterpartyNames.length === 1
                    ? nextFilters.counterpartyNames[0]
                    : null;
            if (nextCounterpartyName !== counterpartyName) {
                onCounterpartyNameChange(nextCounterpartyName);
            }
            if (
                nextFilters.periodStart !== periodStart ||
                nextFilters.periodEnd !== periodEnd
            ) {
                onPeriodChange({
                    start: nextFilters.periodStart,
                    end: nextFilters.periodEnd,
                });
            }
        },
        [
            applicationId,
            applicationIdsKey,
            counterpartyName,
            onApplicationIdChange,
            onApplicationIdsClear,
            onCounterpartyNameChange,
            onPeriodChange,
            periodEnd,
            periodStart,
            syncTemplateIdsToUrl,
        ],
    );

    return (
        <>
            <ScheduleApplicationsTable
                rows={displayRows}
                isLoading={isLoading}
                viewCacheKey={viewCacheKey}
                defaultView={SCHEDULE_APPLICATIONS_PAGE_DEFAULT_VIEW}
                filters={filters}
                onFiltersChange={handleFiltersChange}
                search={search}
                onSearchChange={setSearch}
                counterpartyNames={counterpartyNames}
                lineItemLabels={lineItemLabels}
                templateOptions={templateOptions}
                applicationId={applicationId}
                tabNavigationTop={tabNavigationTop}
                footer={footer}
                rowSelection={rowSelection}
                onRowSelectionChange={setRowSelection}
                isFullPage={isFullPage}
                onOpenReport={openReport}
            />
            {bulkBilling.modal}
            {bulkMilestones.modal}
            {bulkLifecycle.modals}
            {bulkChangePolicy.confirmModal}
        </>
    );
};
